import React, { useState, useEffect, useRef } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  ChevronRight,
  Code2,
  Cpu,
  Terminal,
  Loader2,
} from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { loadTrace, type ExecutionTrace } from "./executionData";
import { CodeDisplay } from "./CodeDisplay";
import { ExecutionVisualizer } from "./ExecutionVisualizer";
import { CHECKOUT_URL } from "../../data/siteLinks";

interface Tab {
  id: string;
  labelKey: string;
  chapterKey: string;
  descriptionKey: string;
}

const tabConfig: Tab[] = [
  {
    id: "basic_agent",
    labelKey: "picoagentsPlayground.tabs.basicAgent.label",
    chapterKey: "picoagentsPlayground.tabs.basicAgent.chapter",
    descriptionKey: "picoagentsPlayground.tabs.basicAgent.description",
  },
  {
    id: "workflows",
    labelKey: "picoagentsPlayground.tabs.workflows.label",
    chapterKey: "picoagentsPlayground.tabs.workflows.chapter",
    descriptionKey: "picoagentsPlayground.tabs.workflows.description",
  },
  {
    id: "multi_agent",
    labelKey: "picoagentsPlayground.tabs.multiAgent.label",
    chapterKey: "picoagentsPlayground.tabs.multiAgent.chapter",
    descriptionKey: "picoagentsPlayground.tabs.multiAgent.description",
  },
];

export const PicoAgentsPlayground: React.FC = () => {
  const { t } = useTranslation("home");
  const [activeTab, setActiveTab] = useState<string>("basic_agent");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentTrace, setCurrentTrace] = useState<ExecutionTrace | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const intervalRef = useRef<number | null>(null);

  // Load trace when tab changes
  useEffect(() => {
    setIsLoading(true);
    setCurrentTime(0);
    setIsPlaying(false);

    loadTrace(activeTab).then((trace) => {
      setCurrentTrace(trace);
      setIsLoading(false);
    });
  }, [activeTab]);

  const duration =
    currentTrace?.events[currentTrace.events.length - 1]?._elapsed_ms || 0;

  useEffect(() => {
    if (isPlaying && currentTime < duration) {
      intervalRef.current = window.setInterval(() => {
        setCurrentTime((prev) => {
          const next = prev + 50 * playbackSpeed;
          if (next >= duration) {
            setIsPlaying(false);
            return duration;
          }
          return next;
        });
      }, 50);
    } else {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, currentTime, duration, playbackSpeed]);

  const handlePlayPause = () => {
    if (currentTime >= duration) {
      setCurrentTime(0);
    }
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setCurrentTime(0);
    setIsPlaying(false);
  };

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const deciseconds = Math.floor((ms % 1000) / 100);
    return `${seconds}.${deciseconds}s`;
  };

  return (
    <div className="my-16 border-b border-secondary pb-16">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-primary mb-4 ">
          {t("picoagentsPlayground.title")}
        </h2>
        <p className="text-lg text-secondary max-w-3xl mx-auto">
          <Trans
            i18nKey="picoagentsPlayground.description"
            ns="home"
            components={{
              picoagents: <span className="text-accent font-semibold" />,
            }}
          />
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-secondary/30 mb-6">
        <nav
          className="flex space-x-8 overflow-x-auto"
          aria-label="Playground tabs"
        >
          {tabConfig.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                pb-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
                ${
                  activeTab === tab.id
                    ? "border-accent text-accent"
                    : "border-transparent text-secondary hover:text-primary hover:border-secondary/50"
                }
              `}
            >
              <div className="flex flex-col items-start">
                <span className="font-semibold">{t(tab.labelKey)}</span>
                <span className="text-xs opacity-70 mt-1">{t(tab.chapterKey)}</span>
              </div>
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="rounded-xl overflow-hidden border border-secondary/30 shadow-lg flex flex-col">
        {/* Code and Output Panels */}
        <div className="grid grid-cols-1 lg:grid-cols-2 h-[450px]">
          {/* Code Panel */}
          <div className="bg-[#1e1e2e] flex flex-col border-b lg:border-b-0 lg:border-r border-secondary/30 min-h-[225px] lg:min-h-0">
            <div className="px-4 py-3 bg-[#181825] border-b border-[#313244] flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-2">
                <Code2 className="w-4 h-4 text-[#cdd6f4]" />
                <span className="text-sm font-medium text-[#cdd6f4]">
                  {t("picoagentsPlayground.codePanel.title")}
                </span>
              </div>
              <span className="text-xs text-[#6c7086]">
                {currentTrace?.filename || "picoagents.py"}
              </span>
            </div>
            <div className="flex-1 overflow-auto">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 text-[#6c7086] animate-spin" />
                </div>
              ) : (
                <CodeDisplay
                  code={currentTrace?.code || ""}
                  highlightLines={[]}
                />
              )}
            </div>
          </div>

          {/* Execution Panel */}
          <div className="bg-primary flex flex-col min-h-[225px] lg:min-h-0">
            <div className="px-4 py-3 bg-secondary/10 border-b border-secondary/30 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-2">
                <Terminal className="w-4 h-4 text-secondary" />
                <span className="text-sm font-medium text-primary">
                  {t("picoagentsPlayground.executionPanel.title")}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Cpu className="w-4 h-4 text-secondary" />
                <span className="text-xs text-secondary">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>
            </div>

            <div className="flex-1 overflow-auto execution-scrollbar">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 text-secondary animate-spin" />
                </div>
              ) : (
                <ExecutionVisualizer
                  events={currentTrace?.events || []}
                  currentTime={currentTime}
                  execution={currentTrace || undefined}
                />
              )}
            </div>
          </div>
        </div>

        {/* Playback Controls */}
        <div className="bg-secondary/10 px-4 sm:px-6 py-3 border-t border-secondary/30 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center space-x-3 flex-wrap gap-y-2">
              <button
                onClick={handlePlayPause}
                disabled={isLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors font-medium disabled:opacity-50"
              >
                {isPlaying ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                <span>{isPlaying ? t("picoagentsPlayground.controls.pause") : t("picoagentsPlayground.controls.play")}</span>
              </button>

              <button
                onClick={handleReset}
                disabled={isLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-secondary/20 text-primary rounded-lg hover:bg-secondary/30 transition-colors disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4" />
                <span>{t("picoagentsPlayground.controls.reset")}</span>
              </button>

              <div className="flex items-center space-x-2">
                <label className="text-sm text-secondary">{t("picoagentsPlayground.controls.speed")}</label>
                <select
                  value={playbackSpeed}
                  onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
                  className="px-2 py-1 border border-secondary/30 rounded text-sm bg-primary text-primary"
                >
                  <option value="0.5">0.5x</option>
                  <option value="1">1x</option>
                  <option value="2">2x</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-secondary hidden sm:inline">
                {t(tabConfig.find((tab) => tab.id === activeTab)?.descriptionKey || "")}
              </span>
              <ChevronRight className="w-4 h-4 text-secondary hidden sm:inline" />
              <a
                href={CHECKOUT_URL}
                className="text-sm font-medium text-accent hover:text-accent/80 transition-colors"
              >
                {t("picoagentsPlayground.controls.buildThis")}
              </a>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-3">
            <div className="relative w-full h-1.5 bg-secondary/30 rounded-full overflow-hidden">
              <div className="absolute inset-0 bg-accent/20 rounded-full" />
              <div
                className="absolute left-0 top-0 h-full bg-accent transition-all duration-75 rounded-full"
                style={{
                  width: `${
                    duration > 0 ? (currentTime / duration) * 100 : 0
                  }%`,
                }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-secondary/60">
              <span>0s</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="mt-8 text-center">
        <p className="text-secondary max-w-2xl mx-auto">
          {t("picoagentsPlayground.footer")}
        </p>
      </div>

      {/* Custom scrollbar styles */}
      <style>{`
        .execution-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .execution-scrollbar::-webkit-scrollbar-track {
          background: transparent;
          border-radius: 4px;
        }
        .execution-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(128, 128, 128, 0.3);
          border-radius: 4px;
        }
        .execution-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(128, 128, 128, 0.5);
        }
        .execution-scrollbar::-webkit-scrollbar-corner {
          background: transparent;
        }
      `}</style>
    </div>
  );
};
